import { createRootRoute, Outlet, useLocation } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import { AdminBadge } from "@/app/components/AdminBadge";
import { AppSidebar } from "@/app/components/AppSidebar";
import { MemberSearchModal } from "@/app/components/MemberSearchModal";
import { ProfileDropdown } from "@/app/components/ProfileDropdown";
import {
  Sidebar,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/app/components/ui/sidebar";
import { useAccessLogger } from "@/app/hooks/useAccessLogger";
import { useAuthGuard } from "@/app/hooks/useAuthGuard";

export const Route = createRootRoute({
  component: RootComponent,
});

function RootComponent() {
  const location = useLocation();
  const { trackPageView } = useAccessLogger();
  const [searchModalOpen, setSearchModalOpen] = useState(false);

  useEffect(() => {
    const targetPath = location.pathname || "/";
    const metadata = location.search ? { search: location.search } : undefined;
    trackPageView(targetPath, metadata);
  }, [location.pathname, location.search, trackPageView]);

  // Global keyboard shortcut for member search (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchModalOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const isPublicPage = [
    "/auth/signin",
    "/auth/signup",
    "/auth/reset-password",
    "/version",
  ].includes(location.pathname);

  if (isPublicPage) {
    return (
      <>
        <Outlet />
        <Toaster position="top-right" />
        <TanStackRouterDevtools />
      </>
    );
  }

  return (
    <>
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
      <MemberSearchModal isOpen={searchModalOpen} onClose={() => setSearchModalOpen(false)} />
      <Toaster position="top-right" />
      <TanStackRouterDevtools />
    </>
  );
}

function DashboardLayout({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated } = useAuthGuard();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <p>You must be signed in to access this page.</p>;
  }

  return (
    <SidebarProvider>
      <Sidebar>
        <AppSidebar />
      </Sidebar>
      <SidebarInset>
        <div className="px-6 py-4">
          <nav className="flex items-center justify-between gap-4">
            <SidebarTrigger />
            <div className="flex items-center gap-2">
              <AdminBadge />
              <ProfileDropdown />
            </div>
          </nav>
        </div>
        {children}
      </SidebarInset>
    </SidebarProvider>
  );
}
